import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, LineChart, Line, ComposedChart, Legend,
} from 'recharts';
import { useIndiaPitchStore } from '../../../state/indiaPitchStore';
import { useIndiaCompanyStore } from '../../../state/indiaCompanyStore';
import { useAuthStore } from '../../../state/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Skeleton } from '../../../components/ui/skeleton';
import CommentThread from '../../../components/ui/CommentThread';
import PitchReportTabBar from '../../../components/workspace/PitchReportTabBar';

const fmt = (v, d = 2) => {
  if (v == null || v === '' || Number.isNaN(Number(v))) return '—';
  return Number(v).toLocaleString('en-IN', { maximumFractionDigits: d });
};

const toArr = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string') {
    if (v.includes('\n')) return v.split('\n').map(s => s.trim()).filter(Boolean);
    return v ? [v] : [];
  }
  return [];
};

const stripCitations = (s) =>
  typeof s === 'string' ? s.replace(/\s*\[[a-f0-9-]{8,}\]/gi, '').replace(/\s*\[unverified\]/gi, '').trim() : s;

const toText = (v) => {
  if (v == null) return '';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return stripCitations(String(v));
  if (Array.isArray(v)) return stripCitations(v.map(toText).filter(Boolean).join(', '));
  if (typeof v === 'object') {
    const raw = v.driver || v.description || v.title || v.name || v.label || v.event || v.text || v.value || JSON.stringify(v);
    return stripCitations(String(raw));
  }
  return stripCitations(String(v));
};

const normalizeTextList = (v) => {
  const items = toArr(v).map(toText).map(s => s.trim()).filter(Boolean);
  if (items.length === 0) return [];
  const nonPunctChars = items.filter(ch => !['.', ',', ';', ':', '!', '?', '-', '(', ')', '/'].includes(ch));
  if (items.length >= 8 && nonPunctChars.length > 0 && nonPunctChars.every(ch => ch.length === 1)) {
    const sentence = items.join('').replace(/\s+/g, ' ').trim();
    return sentence ? [sentence] : [];
  }
  return items;
};

const TOOLTIP_STYLE = { fontSize: 11, background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' };

// ─── Section Header ─────────────────────────────────────────

function SectionTitle({ children }) {
  return <CardTitle className="text-emerald-700 dark:text-emerald-400 text-base">{children}</CardTitle>;
}

// ─── Metric Card ─────────────────────────────────────────────

function MetricCard({ label, value }) {
  return (
    <div className="bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">{toText(label)}</p>
      <p className="text-lg font-bold text-emerald-800 dark:text-emerald-200">{toText(value)}</p>
    </div>
  );
}

// ─── Highlight Card ──────────────────────────────────────────

function HighlightCard({ title, description }) {
  return (
    <div className="p-3 bg-emerald-50 dark:bg-emerald-950/20 border-l-3 border-emerald-600 rounded-r-lg">
      <p className="font-semibold text-sm text-emerald-800 dark:text-emerald-200">{toText(title)}</p>
      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{toText(description)}</p>
    </div>
  );
}

// ─── Generic Financial Table ─────────────────────────────────

function FinTable({ rows, lines, title }) {
  if (!rows || rows.length === 0) return null;
  const periods = rows.map(r => (r.period || '').slice(0, 7));
  return (
    <div>
      {title && <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">{title}</h4>}
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-emerald-800 text-white">
              <th className="text-left py-2 px-3 font-semibold text-[10px] uppercase">Particulars (₹ Cr)</th>
              {periods.map((p, i) => (
                <th key={i} className="text-right py-2 px-3 font-semibold text-[10px] uppercase">{p}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lines.map(line => (
              <tr key={line.key}
                className={`border-b border-muted/20 ${line.highlight ? 'bg-emerald-50 dark:bg-emerald-950/10' : ''}`}
              >
                <td className={`py-1.5 px-3 ${line.bold ? 'font-semibold' : 'text-muted-foreground'}`}>
                  {line.label}
                </td>
                {rows.map((row, i) => {
                  const v = row[line.key];
                  return (
                    <td key={i} className={`py-1.5 px-3 text-right ${line.bold ? 'font-semibold' : ''} ${v != null && v < 0 ? 'text-red-500' : ''}`}>
                      {v != null ? `${fmt(v)}${line.suffix || ''}` : '—'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const PNL_LINES = [
  { key: 'revenue', label: 'Revenue from Operations', bold: true },
  { key: 'other_income', label: 'Other Income' },
  { key: 'total_income', label: 'Total Income', bold: true },
  { key: 'cogs', label: 'Cost of Materials' },
  { key: 'employee_expenses', label: 'Employee Expenses' },
  { key: 'other_expenses', label: 'Other Expenses' },
  { key: 'ebitda', label: 'EBITDA', bold: true, highlight: true },
  { key: 'ebitda_margin_pct', label: 'EBITDA Margin (%)', bold: true, highlight: true, suffix: '%' },
  { key: 'depreciation', label: 'Depreciation' },
  { key: 'ebit', label: 'EBIT' },
  { key: 'finance_cost', label: 'Finance Cost' },
  { key: 'pbt', label: 'Profit Before Tax' },
  { key: 'tax', label: 'Tax' },
  { key: 'pat', label: 'Profit After Tax', bold: true, highlight: true },
  { key: 'pat_margin_pct', label: 'PAT Margin (%)', bold: true, highlight: true, suffix: '%' },
  { key: 'eps', label: 'EPS (₹)', bold: true },
];

const BS_LINES = [
  { key: 'total_assets', label: 'Total Assets', bold: true },
  { key: 'total_equity', label: 'Total Equity', bold: true },
  { key: 'total_debt', label: 'Total Debt' },
  { key: 'cash_and_equivalents', label: 'Cash & Equivalents' },
  { key: 'net_worth', label: 'Net Worth', bold: true, highlight: true },
];

const CF_LINES = [
  { key: 'operating_cf', label: 'Operating Cash Flow', bold: true, highlight: true },
  { key: 'investing_cf', label: 'Investing Cash Flow' },
  { key: 'financing_cf', label: 'Financing Cash Flow' },
  { key: 'net_cf', label: 'Net Change in Cash', bold: true },
  { key: 'capex', label: 'Capital Expenditure' },
  { key: 'fcf', label: 'Free Cash Flow', bold: true, highlight: true },
];

// ─── Peer Comparison Table ───────────────────────────────────

function PeerTable({ peers }) {
  if (!peers || peers.length === 0) return null;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-emerald-800 text-white">
            <th className="text-left py-2 px-3 text-[10px] uppercase font-semibold">Company</th>
            <th className="text-right py-2 px-3 text-[10px] uppercase font-semibold">Price (₹)</th>
            <th className="text-right py-2 px-3 text-[10px] uppercase font-semibold">P/E</th>
            <th className="text-right py-2 px-3 text-[10px] uppercase font-semibold">P/B</th>
            <th className="text-right py-2 px-3 text-[10px] uppercase font-semibold">EV/EBITDA</th>
            <th className="text-right py-2 px-3 text-[10px] uppercase font-semibold">MCap (₹ Cr)</th>
            <th className="text-right py-2 px-3 text-[10px] uppercase font-semibold">Rev Growth</th>
            <th className="text-right py-2 px-3 text-[10px] uppercase font-semibold">ROCE</th>
            <th className="text-right py-2 px-3 text-[10px] uppercase font-semibold">ROE</th>
          </tr>
        </thead>
        <tbody>
          {peers.map((p, i) => (
            <tr key={i} className={`border-b border-muted/20 ${i === 0 ? 'bg-emerald-50 dark:bg-emerald-950/20 font-semibold' : 'hover:bg-muted/20'}`}>
              <td className="py-1.5 px-3 font-medium">{toText(p.company)}</td>
              <td className="py-1.5 px-3 text-right">{p.price != null ? fmt(p.price) : '—'}</td>
              <td className="py-1.5 px-3 text-right">{p.pe != null ? fmt(p.pe) : '—'}</td>
              <td className="py-1.5 px-3 text-right">{p.pb != null ? fmt(p.pb) : '—'}</td>
              <td className="py-1.5 px-3 text-right">{p.ev_ebitda != null ? fmt(p.ev_ebitda) : '—'}</td>
              <td className="py-1.5 px-3 text-right">{p.market_cap != null ? fmt(p.market_cap, 0) : '—'}</td>
              <td className="py-1.5 px-3 text-right">{p.yoy_revenue_growth_pct != null ? `${fmt(p.yoy_revenue_growth_pct)}%` : '—'}</td>
              <td className="py-1.5 px-3 text-right">{p.roce != null ? `${fmt(p.roce)}%` : '—'}</td>
              <td className="py-1.5 px-3 text-right">{p.roe != null ? `${fmt(p.roe)}%` : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Relative Valuation Table ────────────────────────────────

function RelativeValuationTable({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-emerald-800 text-white">
            <th className="text-left py-2 px-3 text-[10px] uppercase font-semibold">Metric</th>
            <th className="text-right py-2 px-3 text-[10px] uppercase font-semibold">Company</th>
            <th className="text-right py-2 px-3 text-[10px] uppercase font-semibold">Peer Avg</th>
            <th className="text-right py-2 px-3 text-[10px] uppercase font-semibold">Sector Avg</th>
            <th className="text-left py-2 px-3 text-[10px] uppercase font-semibold">Assessment</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={i} className="border-b border-muted/20 hover:bg-muted/20">
              <td className="py-1.5 px-3 font-semibold">{toText(item.metric)}</td>
              <td className="py-1.5 px-3 text-right font-medium">{fmt(item.company_value)}</td>
              <td className="py-1.5 px-3 text-right">{fmt(item.peer_average)}</td>
              <td className="py-1.5 px-3 text-right">{fmt(item.sector_average)}</td>
              <td className="py-1.5 px-3 text-muted-foreground">{toText(item.assessment)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Financial Charts ────────────────────────────────────────

function RevenueEbitdaChart({ pnlRows }) {
  if (!pnlRows || pnlRows.length === 0) return null;
  const data = pnlRows.map(r => ({
    period: (r.period || '').slice(0, 7),
    Revenue: r.revenue || 0, EBITDA: r.ebitda || 0, PAT: r.pat || 0,
  }));
  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data} barCategoryGap="20%">
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="period" tick={{ fontSize: 10 }} />
        <YAxis tick={{ fontSize: 10 }} />
        <RechartsTooltip contentStyle={TOOLTIP_STYLE} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="Revenue" fill="#059669" radius={[3, 3, 0, 0]} />
        <Bar dataKey="EBITDA" fill="#10b981" radius={[3, 3, 0, 0]} />
        <Bar dataKey="PAT" fill="#6ee7b7" radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function MarginTrendChart({ pnlRows }) {
  if (!pnlRows || pnlRows.length === 0) return null;
  const data = pnlRows.map(r => ({
    period: (r.period || '').slice(0, 7),
    'EBITDA Margin': r.ebitda_margin_pct || 0, 'PAT Margin': r.pat_margin_pct || 0,
  }));
  return (
    <ResponsiveContainer width="100%" height={220}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="period" tick={{ fontSize: 10 }} />
        <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${v}%`} />
        <RechartsTooltip contentStyle={TOOLTIP_STYLE} formatter={v => `${fmt(v)}%`} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Line type="monotone" dataKey="EBITDA Margin" stroke="#059669" strokeWidth={2} dot />
        <Line type="monotone" dataKey="PAT Margin" stroke="#0ea5e9" strokeWidth={2} dot />
      </LineChart>
    </ResponsiveContainer>
  );
}

function ReturnMetricsChart({ metrics }) {
  if (!metrics || metrics.length === 0) return null;
  const data = metrics.map(r => ({
    period: (r.period || '').slice(0, 7), ROCE: r.roce || 0, ROE: r.roe || 0,
  }));
  return (
    <ResponsiveContainer width="100%" height={220}>
      <ComposedChart data={data}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="period" tick={{ fontSize: 10 }} />
        <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${v}%`} />
        <RechartsTooltip contentStyle={TOOLTIP_STYLE} formatter={v => `${fmt(v)}%`} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="ROCE" fill="#059669" radius={[3, 3, 0, 0]} />
        <Bar dataKey="ROE" fill="#10b981" radius={[3, 3, 0, 0]} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function CashFlowChart({ cfRows }) {
  if (!cfRows || cfRows.length === 0) return null;
  const data = cfRows.map(r => ({
    period: (r.period || '').slice(0, 7),
    'Operating CF': r.operating_cf || 0, 'FCF': r.fcf || 0,
  }));
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} barCategoryGap="20%">
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="period" tick={{ fontSize: 10 }} />
        <YAxis tick={{ fontSize: 10 }} />
        <RechartsTooltip contentStyle={TOOLTIP_STYLE} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="Operating CF" fill="#059669" radius={[3, 3, 0, 0]} />
        <Bar dataKey="FCF" fill="#10b981" radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ─── Sentiment Gauge ─────────────────────────────────────────

function SentimentGauge({ score, sentiment }) {
  const colors = {
    Bullish: 'bg-emerald-500', Neutral: 'bg-blue-500', Bearish: 'bg-red-500',
  };
  const barColor = colors[sentiment] || 'bg-blue-500';
  const pct = Math.min(100, Math.max(0, score || 50));
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>Bearish</span><span>Neutral</span><span>Bullish</span>
      </div>
      <div className="h-3 bg-muted/30 rounded-full overflow-hidden relative">
        <div className={`h-full ${barColor} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-xs text-center text-muted-foreground">Score: {pct}/100</p>
    </div>
  );
}

// ─── Scenario Card ───────────────────────────────────────────

function ScenarioCard({ label, scenario, color }) {
  const colors = {
    bull: 'bg-green-50 dark:bg-green-950/20 border-l-green-600',
    base: 'bg-blue-50 dark:bg-blue-950/20 border-l-blue-600',
    bear: 'bg-red-50 dark:bg-red-950/20 border-l-red-600',
  };
  return (
    <div className={`p-4 rounded-r-lg border-l-4 ${colors[color] || ''}`}>
      <h4 className="font-semibold text-sm mb-1">{label}</h4>
      <p className="text-sm leading-relaxed">{toText(scenario?.narrative)}</p>
      {scenario?.triggers?.length > 0 && (
        <ul className="mt-2 space-y-1">
          {scenario.triggers.map((t, i) => (
            <li key={i} className="text-xs text-muted-foreground flex gap-1"><span>•</span><span>{toText(t)}</span></li>
          ))}
        </ul>
      )}
      {scenario?.target_range && (
        <p className="text-xs font-medium mt-2 text-muted-foreground">Target Range: {toText(scenario.target_range)}</p>
      )}
    </div>
  );
}

// ─── Product Segments Table ──────────────────────────────────

function ProductTable({ segments }) {
  if (!segments || segments.length === 0) return null;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-emerald-800 text-white">
            <th className="text-left py-2 px-3 text-[10px] uppercase font-semibold">Segment</th>
            <th className="text-left py-2 px-3 text-[10px] uppercase font-semibold">Key Products</th>
            <th className="text-left py-2 px-3 text-[10px] uppercase font-semibold">Volume / Margin</th>
            <th className="text-left py-2 px-3 text-[10px] uppercase font-semibold">Strategic Role</th>
          </tr>
        </thead>
        <tbody>
          {segments.map((s, i) => (
            <tr key={i} className="border-b border-muted/20 hover:bg-muted/20">
              <td className="py-2 px-3 font-semibold">{toText(s.segment)}</td>
              <td className="py-2 px-3">{toText(s.key_products)}</td>
              <td className="py-2 px-3">{toText(s.volume_margin_profile)}</td>
              <td className="py-2 px-3 text-muted-foreground">{toText(s.strategic_role)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Shareholding Bar ────────────────────────────────────────

function ShareholdingBar({ holders }) {
  if (!holders || Object.keys(holders).length === 0) return null;
  const segments = Object.entries(holders).map(([label, pct]) => ({
    label, pct: parseFloat(pct) || 0,
  })).filter(s => s.pct > 0);
  if (segments.length === 0) return null;

  const barColors = [
    'bg-emerald-600', 'bg-emerald-400', 'bg-sky-500', 'bg-amber-500',
    'bg-violet-500', 'bg-rose-400',
  ];

  return (
    <div className="space-y-2">
      <div className="flex h-6 rounded-full overflow-hidden">
        {segments.map((s, i) => (
          <div key={i} className={`${barColors[i % barColors.length]} transition-all`}
            style={{ width: `${s.pct}%` }} title={`${s.label}: ${s.pct}%`} />
        ))}
      </div>
      <div className="flex flex-wrap gap-3">
        {segments.map((s, i) => (
          <div key={i} className="flex items-center gap-1.5 text-xs">
            <div className={`w-2.5 h-2.5 rounded-full ${barColors[i % barColors.length]}`} />
            <span className="text-muted-foreground">{s.label}:</span>
            <span className="font-semibold">{fmt(s.pct, 1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Status Screen ───────────────────────────────────────────

const PIPELINE_STEPS = [
  { label: 'Building model pack', icon: '📦' },
  { label: 'Fetching peer data', icon: '📊' },
  { label: 'Analyzing X signals', icon: '𝕏' },
  { label: 'Scanning news', icon: '📰' },
  { label: 'Writing report', icon: '✍️' },
  { label: 'Quality checks', icon: '✅' },
];

function GeneratingStatus({ error }) {
  const [elapsed, setElapsed] = React.useState(0);
  const [activeStep, setActiveStep] = React.useState(0);

  React.useEffect(() => {
    const timer = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  React.useEffect(() => {
    const stepTimer = setInterval(() => {
      setActiveStep(s => (s < PIPELINE_STEPS.length - 1 ? s + 1 : s));
    }, 45000);
    return () => clearInterval(stepTimer);
  }, []);

  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  const timeStr = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;

  return (
    <div className="flex flex-col items-center justify-center min-h-[500px] px-4">
      <div className="w-20 h-20 rounded-full border-4 border-emerald-200 dark:border-emerald-900 border-t-emerald-500 animate-spin mb-8" />

      <h2 className="text-2xl font-bold text-center mb-3">
        Generating Pitch Report
      </h2>

      <p className="text-muted-foreground text-sm text-center max-w-sm mb-2">
        Building a comprehensive equity research report with financial analysis, sentiment signals, and valuation.
      </p>

      <p className="text-xs text-muted-foreground/60 mb-8">
        This typically takes 4–6 minutes &middot; Elapsed: {timeStr}
      </p>

      <div className="w-full max-w-md space-y-2">
        {PIPELINE_STEPS.map((step, i) => {
          const done = i < activeStep;
          const active = i === activeStep;
          return (
            <div
              key={step.label}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-300 ${
                active
                  ? 'bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800'
                  : done
                  ? 'opacity-60'
                  : 'opacity-30'
              }`}
            >
              <span className="text-base w-6 text-center shrink-0">{step.icon}</span>
              <span className={`text-sm flex-1 ${active ? 'font-semibold text-emerald-700 dark:text-emerald-300' : ''}`}>
                {step.label}
              </span>
              {done && <span className="text-emerald-500 text-sm">✓</span>}
              {active && (
                <div className="w-4 h-4 rounded-full border-2 border-emerald-300 border-t-emerald-600 animate-spin shrink-0" />
              )}
            </div>
          );
        })}
      </div>

      {error && (
        <div className="mt-6 text-red-500 text-sm p-4 bg-red-50 dark:bg-red-950/20 rounded-lg max-w-md border border-red-200 dark:border-red-800">
          {error}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────

export default function PitchReportPage() {
  const { symbol } = useParams();
  const navigate = useNavigate();

  const {
    runId, status, error, report, quality,
    loading, generating,
    generateReport, fetchLatestReport, downloadPdf, reset,
    approvalStatus, approvalLoading, submitForReview, approveReport, rejectReport,
  } = useIndiaPitchStore();

  const { companyData, fetchCompany } = useIndiaCompanyStore();
  const userId = useAuthStore(s => s.user?.id);
  const [isin, setIsin] = useState(null);
  const [generating_, setGenerating] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);

  useEffect(() => {
    if (symbol) fetchCompany(symbol);
    return () => reset();
  }, [symbol]);

  useEffect(() => {
    if (companyData?.db?.isin) {
      setIsin(companyData.db.isin);
      fetchLatestReport(companyData.db.isin);
    }
  }, [companyData]);

  const handleGenerate = async () => {
    if (!isin) return;
    setGenerating(true);
    try { await generateReport(isin); } catch {}
    setGenerating(false);
  };

  const r = report;
  const meta = r?.meta;
  const fo = r?.financial_overview;
  const pc = r?.peer_comparison;
  const co = r?.company_overview;
  const io = r?.industry_overview;
  const sa = r?.sentiment_analysis;
  const bsa = r?.balance_sheet_analysis;
  const cfa = r?.cash_flow_analysis;
  const rawVal = r?.valuation;
  const val = rawVal ? { ...rawVal } : null;
  if (val) {
    if (val.fair_value_range && typeof val.fair_value_range !== 'object') {
      try { val.fair_value_range = JSON.parse(String(val.fair_value_range).replace(/'/g, '"')); } catch { val.fair_value_range = null; }
    }
    if (val.dcf_summary && typeof val.dcf_summary !== 'object') {
      try { val.dcf_summary = JSON.parse(String(val.dcf_summary).replace(/'/g, '"')); } catch { val.dcf_summary = null; }
    }
  }

  const recStyle = r?.conclusion?.recommendation_style;
  const recColor = recStyle === 'Positive' ? 'bg-emerald-600' :
                   recStyle === 'Cautious' ? 'bg-amber-500' : 'bg-blue-600';

  if (status === 'RUNNING' || generating_) {
    return (
      <div>
        <PitchReportTabBar activeSymbol={symbol} />
        <div className="p-4 md:p-6 max-w-5xl mx-auto">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-4">← Back</Button>
          <GeneratingStatus error={error} />
        </div>
      </div>
    );
  }

  return (
    <div>
    <PitchReportTabBar activeSymbol={symbol} />
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/public/india-company/${symbol}`)}>
          ← Back to {symbol}
        </Button>
        <div className="flex gap-2 flex-wrap items-center">
          {report && approvalStatus && (
            <Badge className={
              approvalStatus === 'approved' ? 'bg-emerald-600 text-white' :
              approvalStatus === 'rejected' ? 'bg-red-500 text-white' :
              approvalStatus === 'pending_review' ? 'bg-amber-500 text-white' :
              'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
            }>
              {approvalStatus === 'draft' ? 'Draft' :
               approvalStatus === 'pending_review' ? 'Pending Review' :
               approvalStatus === 'approved' ? 'Approved' : 'Rejected'}
            </Badge>
          )}
          {report && quality && (
            <Badge variant={quality.overall_passes ? 'default' : 'destructive'}
              className={quality.overall_passes ? 'bg-emerald-600' : ''}>
              Quality: {quality.overall_passes ? 'Pass' : 'Issues'}
            </Badge>
          )}
          {report && approvalStatus === 'draft' && (
            <Button size="sm" variant="outline" className="text-xs" disabled={approvalLoading}
              onClick={() => submitForReview(runId)}>
              Submit for Review
            </Button>
          )}
          {report && approvalStatus === 'pending_review' && (
            <>
              <Button size="sm" className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white" disabled={approvalLoading}
                onClick={() => approveReport(runId, userId)}>
                Approve
              </Button>
              <Button size="sm" variant="destructive" className="text-xs" disabled={approvalLoading}
                onClick={() => {
                  if (showRejectInput && rejectNotes.trim()) {
                    rejectReport(runId, userId, rejectNotes);
                    setShowRejectInput(false);
                    setRejectNotes('');
                  } else {
                    setShowRejectInput(true);
                  }
                }}>
                Reject
              </Button>
            </>
          )}
          {report && (
            <Button size="sm" variant="outline" onClick={() => downloadPdf(runId)}>Export PDF</Button>
          )}
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={handleGenerate} disabled={!isin || generating_ || status === 'RUNNING'}>
            {report ? 'Regenerate' : 'Generate Report'}
          </Button>
        </div>
      </div>

      {showRejectInput && (
        <div className="flex gap-2 items-end">
          <input
            value={rejectNotes}
            onChange={e => setRejectNotes(e.target.value)}
            placeholder="Rejection notes…"
            className="flex-1 text-xs px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-1 focus:ring-red-500"
          />
          <Button size="sm" variant="destructive" className="text-xs" disabled={!rejectNotes.trim()}
            onClick={() => { rejectReport(runId, userId, rejectNotes); setShowRejectInput(false); setRejectNotes(''); }}>
            Confirm Reject
          </Button>
          <Button size="sm" variant="ghost" className="text-xs" onClick={() => setShowRejectInput(false)}>Cancel</Button>
        </div>
      )}

      {loading && (
        <div className="space-y-3">
          <Skeleton className="h-8 w-64" /><Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" /><Skeleton className="h-48 w-full" />
        </div>
      )}

      {!loading && !report && status !== 'RUNNING' && (
        <Card>
          <CardContent className="p-12 text-center space-y-4">
            <div className="text-5xl mb-2">📊</div>
            <h2 className="text-xl font-bold">Institutional-Grade Stock Pitch Report</h2>
            <p className="text-muted-foreground max-w-lg mx-auto text-sm">
              Generate a comprehensive equity research report for {symbol?.toUpperCase()} with detailed
              financial analysis, sentiment signals, peer comparison, DCF valuation, and investment thesis.
            </p>
            <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={handleGenerate} disabled={!isin}>Generate Report</Button>
          </CardContent>
        </Card>
      )}

      {/* ── REPORT CONTENT ── */}
      {report && (
        <div className="space-y-4">
          {/* Title Card */}
          <Card className="border-emerald-200 dark:border-emerald-800">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-emerald-800 dark:text-emerald-200">{meta?.company_name}</h1>
                  <div className="flex gap-2 mt-1 text-sm text-muted-foreground flex-wrap">
                    <Badge variant="outline">{meta?.nse_symbol}.NS</Badge>
                    <span>ISIN: {meta?.isin}</span>
                    <span>{meta?.currency}</span>
                  </div>
                </div>
                <div className="flex gap-2 items-center">
                  {sa?.overall_sentiment && (
                    <Badge className={`text-white text-xs px-3 py-1 ${
                      sa.overall_sentiment === 'Bullish' ? 'bg-emerald-500' :
                      sa.overall_sentiment === 'Bearish' ? 'bg-red-500' : 'bg-blue-500'
                    }`}>{sa.overall_sentiment}</Badge>
                  )}
                  {recStyle && <Badge className={`${recColor} text-white text-sm px-4 py-1`}>{recStyle}</Badge>}
                </div>
              </div>
              <div className="flex gap-3 mt-2 text-xs text-muted-foreground flex-wrap">
                <span>Data as of {meta?.data_as_of_date}</span>
                <span>Generated {meta?.generated_at?.slice(0, 10)}</span>
                {val?.target_price && <span className="font-semibold text-emerald-600">Target: ₹{fmt(val.target_price, 0)}</span>}
                {val?.margin_of_safety && <span>MoS: {val.margin_of_safety}</span>}
              </div>
            </CardContent>
          </Card>

          {/* Executive Summary */}
          {r.executive_summary && (
            <Card>
              <CardHeader className="pb-2"><SectionTitle>Executive Summary</SectionTitle></CardHeader>
              <CardContent className="pt-0 space-y-3">
                <h3 className="text-base font-semibold">{toText(r.executive_summary.headline)}</h3>
                <p className="text-sm leading-relaxed whitespace-pre-line">{toText(r.executive_summary.narrative)}</p>
                {toArr(r.executive_summary.key_metrics).length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
                    {toArr(r.executive_summary.key_metrics).map((m, i) => (
                      <MetricCard key={i} label={m?.label} value={m?.value} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Investment Highlights */}
          {toArr(r.investment_highlights).length > 0 && (
            <Card>
              <CardHeader className="pb-2"><SectionTitle>Key Investment Highlights</SectionTitle></CardHeader>
              <CardContent className="pt-0 space-y-2">
                {toArr(r.investment_highlights).map((h, i) => (
                  <HighlightCard key={i} title={h.title} description={h.description} />
                ))}
              </CardContent>
            </Card>
          )}

          {/* ── Sentiment Analysis ── */}
          {sa && (
            <Card>
              <CardHeader className="pb-2"><SectionTitle>Sentiment Analysis</SectionTitle></CardHeader>
              <CardContent className="pt-0 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <SentimentGauge score={sa.sentiment_score} sentiment={sa.overall_sentiment} />
                  </div>
                  <div className="flex flex-col gap-2">
                    {sa.social_momentum && (
                      <div className="bg-muted/20 rounded-lg p-3 text-center">
                        <p className="text-[10px] uppercase text-muted-foreground">Social Momentum</p>
                        <p className={`text-lg font-bold ${
                          sa.social_momentum === 'Accelerating' ? 'text-emerald-600' :
                          sa.social_momentum === 'Declining' ? 'text-red-500' : 'text-blue-500'
                        }`}>{sa.social_momentum}</p>
                      </div>
                    )}
                  </div>
                </div>

                {sa.x_signal_summary && (
                  <div>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-1">X / Twitter Signals</h4>
                    <p className="text-sm leading-relaxed whitespace-pre-line">{toText(sa.x_signal_summary)}</p>
                  </div>
                )}

                {sa.news_summary && (
                  <div>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-1">News Sentiment</h4>
                    <p className="text-sm leading-relaxed whitespace-pre-line">{toText(sa.news_summary)}</p>
                  </div>
                )}

                {toArr(sa.key_narratives).length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Key Narratives</h4>
                    <div className="flex flex-wrap gap-2">
                      {toArr(sa.key_narratives).map((n, i) => {
                        const sent = n?.sentiment || 'neutral';
                        const color = sent === 'positive' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                          : sent === 'negative' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                          : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
                        return (
                          <span key={i} className={`text-xs px-2.5 py-1 rounded-full font-medium ${color}`}>
                            {toText(n?.narrative || n)}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Industry Overview */}
          {io && (
            <Card>
              <CardHeader className="pb-2"><SectionTitle>Industry Overview</SectionTitle></CardHeader>
              <CardContent className="pt-0 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <MetricCard label="Market Size" value={toText(io.market_size)} />
                  <MetricCard label="Growth Rate" value={toText(io.growth_rate)} />
                </div>
                {normalizeTextList(io.key_growth_drivers).length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-1">Key Growth Drivers</h4>
                    <ul className="text-sm space-y-1 ml-4">{normalizeTextList(io.key_growth_drivers).map((d, i) => (
                      <li key={i} className="list-disc text-muted-foreground">{toText(d)}</li>
                    ))}</ul>
                  </div>
                )}
                <div>
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-1">Industry Structure</h4>
                  <p className="text-sm leading-relaxed">{toText(io.industry_structure)}</p>
                </div>
                {normalizeTextList(io.entry_barriers).length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-1">Entry Barriers</h4>
                    <ul className="text-sm space-y-1 ml-4">{normalizeTextList(io.entry_barriers).map((b, i) => (
                      <li key={i} className="list-disc text-muted-foreground">{toText(b)}</li>
                    ))}</ul>
                  </div>
                )}
                {io.india_relevance && (
                  <div>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-1">India Relevance</h4>
                    <p className="text-sm leading-relaxed">{toText(io.india_relevance)}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Company Overview */}
          {co && (
            <Card>
              <CardHeader className="pb-2"><SectionTitle>Company Overview</SectionTitle></CardHeader>
              <CardContent className="pt-0 space-y-3">
                <p className="text-sm leading-relaxed">{toText(co.description)}</p>
                {toArr(co.history_milestones).length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Key Milestones</h4>
                    <div className="space-y-1">
                      {toArr(co.history_milestones).map((m, i) => (
                        <div key={i} className="flex gap-3 py-1.5 border-b border-muted/20 last:border-0">
                          <span className="font-bold text-emerald-600 text-sm min-w-[50px]">{toText(m?.year)}</span>
                          <span className="text-sm">{toText(m?.event)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {toArr(co.product_segments).length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Product Segments</h4>
                    <ProductTable segments={toArr(co.product_segments)} />
                  </div>
                )}
                {toArr(co.key_clients).length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-1">Key Clients</h4>
                    <div className="flex flex-wrap gap-1">
                      {toArr(co.key_clients).map((c, i) => <Badge key={i} variant="outline" className="text-xs">{toText(c)}</Badge>)}
                    </div>
                  </div>
                )}
                {toArr(co.management).length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Management Team</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs border-collapse">
                        <thead><tr className="bg-muted/50">
                          <th className="text-left py-2 px-3 font-semibold">Name</th>
                          <th className="text-left py-2 px-3 font-semibold">Designation</th>
                          <th className="text-left py-2 px-3 font-semibold">Background</th>
                        </tr></thead>
                        <tbody>{toArr(co.management).map((m, i) => (
                          <tr key={i} className="border-b border-muted/20">
                            <td className="py-1.5 px-3 font-semibold">{toText(m?.name)}</td>
                            <td className="py-1.5 px-3">{toText(m?.designation)}</td>
                            <td className="py-1.5 px-3 text-muted-foreground">{toText(m?.background)}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* ── Financial Overview ── */}
          {fo && (
            <Card>
              <CardHeader className="pb-2"><SectionTitle>Financial Overview</SectionTitle></CardHeader>
              <CardContent className="pt-0 space-y-4">
                {toArr(fo.financial_highlights_cards).length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {toArr(fo.financial_highlights_cards).map((h, i) => (
                      <MetricCard key={i} label={h?.metric} value={`${toText(h?.value)} ${h?.trend ? `(${h.trend})` : ''}`} />
                    ))}
                  </div>
                )}

                <FinTable rows={toArr(fo.pnl_table)} lines={PNL_LINES} title="Profit & Loss Statement" />

                {toArr(fo.pnl_table).length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Revenue, EBITDA & PAT (₹ Cr)</h4>
                      <RevenueEbitdaChart pnlRows={toArr(fo.pnl_table)} />
                    </div>
                    <div>
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Margin Trends</h4>
                      <MarginTrendChart pnlRows={toArr(fo.pnl_table)} />
                    </div>
                  </div>
                )}

                <FinTable rows={toArr(fo.balance_sheet_table)} lines={BS_LINES} title="Balance Sheet Summary" />

                {toArr(fo.return_metrics).length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">ROCE & ROE</h4>
                    <ReturnMetricsChart metrics={toArr(fo.return_metrics)} />
                  </div>
                )}

                {fo.revenue_growth_narrative && (
                  <div><h4 className="text-xs font-semibold text-muted-foreground uppercase mb-1">Revenue Analysis</h4>
                  <p className="text-sm leading-relaxed">{toText(fo.revenue_growth_narrative)}</p></div>
                )}
                {fo.margin_narrative && (
                  <div><h4 className="text-xs font-semibold text-muted-foreground uppercase mb-1">Margin Analysis</h4>
                  <p className="text-sm leading-relaxed">{toText(fo.margin_narrative)}</p></div>
                )}
                {fo.debt_narrative && (
                  <div><h4 className="text-xs font-semibold text-muted-foreground uppercase mb-1">Debt & Capital Structure</h4>
                  <p className="text-sm leading-relaxed">{toText(fo.debt_narrative)}</p></div>
                )}
              </CardContent>
            </Card>
          )}

          {/* ── Balance Sheet Analysis ── */}
          {bsa && bsa.narrative && (
            <Card>
              <CardHeader className="pb-2"><SectionTitle>Balance Sheet Analysis</SectionTitle></CardHeader>
              <CardContent className="pt-0 space-y-3">
                <div className="flex gap-3 flex-wrap">
                  {bsa.leverage_trend && (
                    <div className="bg-muted/20 rounded-lg px-4 py-2">
                      <p className="text-[10px] uppercase text-muted-foreground">Leverage</p>
                      <p className={`text-sm font-bold ${bsa.leverage_trend === 'Improving' ? 'text-emerald-600' : bsa.leverage_trend === 'Deteriorating' ? 'text-red-500' : ''}`}>
                        {bsa.leverage_trend}
                      </p>
                    </div>
                  )}
                  {bsa.liquidity_assessment && (
                    <div className="bg-muted/20 rounded-lg px-4 py-2">
                      <p className="text-[10px] uppercase text-muted-foreground">Liquidity</p>
                      <p className={`text-sm font-bold ${bsa.liquidity_assessment === 'Strong' ? 'text-emerald-600' : bsa.liquidity_assessment === 'Weak' ? 'text-red-500' : ''}`}>
                        {bsa.liquidity_assessment}
                      </p>
                    </div>
                  )}
                </div>
                <p className="text-sm leading-relaxed whitespace-pre-line">{toText(bsa.narrative)}</p>
              </CardContent>
            </Card>
          )}

          {/* ── Cash Flow Analysis ── */}
          {cfa && (
            <Card>
              <CardHeader className="pb-2"><SectionTitle>Cash Flow Analysis</SectionTitle></CardHeader>
              <CardContent className="pt-0 space-y-4">
                {cfa.fcf_quality && (
                  <div className="bg-muted/20 rounded-lg px-4 py-2 inline-block">
                    <p className="text-[10px] uppercase text-muted-foreground">FCF Quality</p>
                    <p className={`text-sm font-bold ${cfa.fcf_quality === 'Strong' ? 'text-emerald-600' : cfa.fcf_quality === 'Weak' ? 'text-red-500' : ''}`}>
                      {cfa.fcf_quality}
                    </p>
                  </div>
                )}

                <FinTable rows={toArr(cfa.cf_table)} lines={CF_LINES} title="Cash Flow Statement" />

                {toArr(cfa.cf_table).length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Operating CF & FCF Trend (₹ Cr)</h4>
                    <CashFlowChart cfRows={toArr(cfa.cf_table)} />
                  </div>
                )}

                {cfa.narrative && (
                  <p className="text-sm leading-relaxed whitespace-pre-line">{toText(cfa.narrative)}</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Peer Comparison */}
          {pc && (
            <Card>
              <CardHeader className="pb-2"><SectionTitle>Peer Comparison</SectionTitle></CardHeader>
              <CardContent className="pt-0 space-y-3">
                <PeerTable peers={toArr(pc.peers)} />

                {toArr(pc.relative_valuation).length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Relative Valuation</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs border-collapse">
                        <thead><tr className="bg-muted/50">
                          <th className="text-left py-2 px-3 font-semibold">Metric</th>
                          <th className="text-right py-2 px-3 font-semibold">Company</th>
                          <th className="text-right py-2 px-3 font-semibold">Peer Median</th>
                          <th className="text-right py-2 px-3 font-semibold">Premium/Discount</th>
                        </tr></thead>
                        <tbody>{toArr(pc.relative_valuation).map((rv, i) => {
                          const pct = rv.premium_discount_pct;
                          return (
                            <tr key={i} className="border-b border-muted/20">
                              <td className="py-1.5 px-3 font-semibold">{toText(rv.metric)}</td>
                              <td className="py-1.5 px-3 text-right">{fmt(rv.company_value)}</td>
                              <td className="py-1.5 px-3 text-right">{fmt(rv.peer_median)}</td>
                              <td className={`py-1.5 px-3 text-right font-medium ${pct > 0 ? 'text-red-500' : pct < 0 ? 'text-emerald-600' : ''}`}>
                                {pct != null ? `${pct > 0 ? '+' : ''}${fmt(pct)}%` : '—'}
                              </td>
                            </tr>
                          );
                        })}</tbody>
                      </table>
                    </div>
                  </div>
                )}

                {pc.positioning_narrative && (
                  <div className="bg-muted/20 p-3 rounded-lg">
                    <p className="text-sm leading-relaxed whitespace-pre-line">{toText(pc.positioning_narrative)}</p>
                  </div>
                )}
                {pc.industry_average_pe && (
                  <p className="text-xs text-muted-foreground">Industry Average P/E: {toText(pc.industry_average_pe)}</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Shareholding */}
          {r.major_holders && Object.keys(r.major_holders || {}).length > 0 && (
            <Card>
              <CardHeader className="pb-2"><SectionTitle>Shareholding Pattern</SectionTitle></CardHeader>
              <CardContent className="pt-0">
                <ShareholdingBar holders={r.major_holders} />
              </CardContent>
            </Card>
          )}

          {/* ── Enhanced Valuation ── */}
          {val && (
            <Card>
              <CardHeader className="pb-2"><SectionTitle>Valuation</SectionTitle></CardHeader>
              <CardContent className="pt-0 space-y-4">
                <div className="flex gap-3 flex-wrap">
                  {val.margin_of_safety && (
                    <div className={`rounded-lg px-4 py-2 ${
                      val.margin_of_safety === 'High' ? 'bg-emerald-50 dark:bg-emerald-950/20' :
                      val.margin_of_safety === 'Negative' ? 'bg-red-50 dark:bg-red-950/20' :
                      'bg-muted/20'
                    }`}>
                      <p className="text-[10px] uppercase text-muted-foreground">Margin of Safety</p>
                      <p className={`text-sm font-bold ${
                        val.margin_of_safety === 'High' ? 'text-emerald-600' :
                        val.margin_of_safety === 'Negative' ? 'text-red-500' : ''
                      }`}>{val.margin_of_safety}</p>
                    </div>
                  )}
                  {val.fair_value_range && (
                    <div className="bg-muted/20 rounded-lg px-4 py-2">
                      <p className="text-[10px] uppercase text-muted-foreground">Fair Value Range</p>
                      <p className="text-sm font-bold">
                        ₹{fmt(val.fair_value_range.low, 0)} – ₹{fmt(val.fair_value_range.high, 0)}
                      </p>
                    </div>
                  )}
                  {val.target_price && (
                    <div className="bg-emerald-50 dark:bg-emerald-950/20 rounded-lg px-4 py-2">
                      <p className="text-[10px] uppercase text-muted-foreground">Target Price</p>
                      <p className="text-lg font-bold text-emerald-700 dark:text-emerald-300">₹{fmt(val.target_price, 0)}</p>
                    </div>
                  )}
                </div>

                {val.approach && (
                  <p className="text-sm"><strong>Methodology:</strong> {toText(val.approach)}</p>
                )}
                {val.summary && (
                  <p className="text-sm leading-relaxed whitespace-pre-line">{toText(val.summary)}</p>
                )}

                <RelativeValuationTable items={toArr(val.relative_valuation_table)} />

                {val.dcf_summary && (
                  <div className="bg-muted/20 p-4 rounded-lg space-y-2">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase">DCF Cross-Check</h4>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center">
                      {val.dcf_summary.wacc_assumption && (
                        <div><p className="text-[10px] text-muted-foreground uppercase">WACC</p><p className="text-sm font-bold">{toText(val.dcf_summary.wacc_assumption)}</p></div>
                      )}
                      {val.dcf_summary.terminal_growth && (
                        <div><p className="text-[10px] text-muted-foreground uppercase">Terminal Growth</p><p className="text-sm font-bold">{toText(val.dcf_summary.terminal_growth)}</p></div>
                      )}
                      {val.dcf_summary.intrinsic_value_per_share && (
                        <div><p className="text-[10px] text-muted-foreground uppercase">Intrinsic Value</p><p className="text-sm font-bold text-emerald-600">₹{fmt(val.dcf_summary.intrinsic_value_per_share, 0)}</p></div>
                      )}
                      {val.dcf_summary.current_price && (
                        <div><p className="text-[10px] text-muted-foreground uppercase">Current Price</p><p className="text-sm font-bold">₹{fmt(val.dcf_summary.current_price, 0)}</p></div>
                      )}
                      {val.dcf_summary.upside_pct != null && (
                        <div><p className="text-[10px] text-muted-foreground uppercase">Upside</p>
                        <p className={`text-sm font-bold ${val.dcf_summary.upside_pct > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                          {val.dcf_summary.upside_pct > 0 ? '+' : ''}{fmt(val.dcf_summary.upside_pct)}%
                        </p></div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Catalysts */}
          {toArr(r.catalysts).length > 0 && (
            <Card>
              <CardHeader className="pb-2"><SectionTitle>Catalysts</SectionTitle></CardHeader>
              <CardContent className="pt-0 space-y-2">
                {toArr(r.catalysts).map((c, i) => (
                  <div key={i} className="flex justify-between items-start p-2 bg-emerald-50 dark:bg-emerald-950/10 rounded">
                    <span className="text-sm flex-1">{toText(c?.catalyst)}</span>
                    <div className="flex gap-1 ml-2 shrink-0">
                      {c?.impact && <Badge variant={c.impact === 'High' ? 'default' : 'outline'} className={`text-[10px] ${c.impact === 'High' ? 'bg-emerald-600' : ''}`}>{c.impact}</Badge>}
                      <Badge variant="outline" className="text-[10px]">{toText(c?.timeframe)}</Badge>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Risks */}
          {toArr(r.risks).length > 0 && (
            <Card>
              <CardHeader className="pb-2"><SectionTitle>Key Risks</SectionTitle></CardHeader>
              <CardContent className="pt-0 space-y-2">
                {toArr(r.risks).map((ri, i) => (
                  <div key={i} className="p-2 border-b border-muted/20 last:border-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium flex-1">{toText(ri?.risk)}</p>
                      <div className="flex gap-1 shrink-0">
                        {ri.severity && (
                          <Badge variant={toText(ri.severity) === 'High' ? 'destructive' : toText(ri.severity) === 'Medium' ? 'default' : 'outline'}
                            className="text-[10px]">{toText(ri.severity)}</Badge>
                        )}
                        {ri.probability && (
                          <Badge variant="outline" className="text-[10px]">P: {toText(ri.probability)}</Badge>
                        )}
                      </div>
                    </div>
                    {ri.mitigants && <p className="text-xs text-emerald-600 mt-1">Mitigant: {toText(ri.mitigants)}</p>}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Bull / Base / Bear */}
          {r.bull_base_bear && (
            <Card>
              <CardHeader className="pb-2"><SectionTitle>Scenario Analysis</SectionTitle></CardHeader>
              <CardContent className="pt-0 space-y-3">
                <ScenarioCard label="Bull Case" scenario={r.bull_base_bear.bull} color="bull" />
                <ScenarioCard label="Base Case" scenario={r.bull_base_bear.base} color="base" />
                <ScenarioCard label="Bear Case" scenario={r.bull_base_bear.bear} color="bear" />
              </CardContent>
            </Card>
          )}

          {/* Conclusion */}
          {r.conclusion && (
            <Card>
              <CardHeader className="pb-2"><SectionTitle>Conclusion</SectionTitle></CardHeader>
              <CardContent className="pt-0 space-y-2">
                <p className="text-sm leading-relaxed">{toText(r.conclusion.summary)}</p>
                {toArr(r.conclusion?.key_takeaways).length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-1">Key Takeaways</h4>
                    <ul className="text-sm space-y-1 ml-4">{toArr(r.conclusion.key_takeaways).map((t, i) => (
                      <li key={i} className="list-disc">{toText(t)}</li>
                    ))}</ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Disclaimer */}
          {meta?.disclaimer && (
            <div className="p-4 bg-muted/30 rounded-lg text-xs text-muted-foreground leading-relaxed border">
              <strong>Disclaimer:</strong> {meta.disclaimer}
            </div>
          )}

          {/* Comments */}
          {runId && <CommentThread entityType="report" entityId={runId} />}
        </div>
      )}
    </div>
    </div>
  );
}
